import React, { useState, useEffect } from 'react';
import {
  Phone,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  RefreshCw,
  Wifi,
  WifiOff,
  Settings,
  TestTube,
  Copy,
  Eye,
  EyeOff,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { sipTrunksAPI } from '../services/api';

const sipProviderTemplates = [
  { id: "twilio", name: "Twilio", host: "sip.twilio.com", port: 5060, transport: "UDP", authRequired: true, codecs: ["G.711u", "G.711a", "G.729"], logo: "twilio" },
  { id: "amazon-chime", name: "Amazon Chime", host: "service.chime.aws", port: 5060, transport: "TLS", authRequired: true, codecs: ["G.711u", "Opus"], logo: "aws" },
  { id: "bandwidth", name: "Bandwidth", host: "sip.bandwidth.com", port: 5060, transport: "UDP", authRequired: true, codecs: ["G.711u", "G.711a"], logo: "bandwidth" },
  { id: "voipms", name: "VoIP.ms", host: "[server].voip.ms", port: 5060, transport: "UDP", authRequired: true, codecs: ["G.711u", "G.711a", "G.729", "GSM"], logo: "voipms" },
  { id: "flowroute", name: "Flowroute", host: "us-west-or.sip.flowroute.com", port: 5060, transport: "UDP", authRequired: true, codecs: ["G.711u", "G.711a"], logo: "flowroute" },
  { id: "telnyx", name: "Telnyx", host: "sip.telnyx.com", port: 5060, transport: "TLS", authRequired: true, codecs: ["G.711u", "G.711a", "Opus"], logo: "telnyx" },
  { id: "generic", name: "Generic SIP", host: "", port: 5060, transport: "UDP", authRequired: true, codecs: ["G.711u", "G.711a", "G.729"], logo: "generic" }
];

const SIPTrunks = () => {
  const [sipTrunks, setSipTrunks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTrunk, setSelectedTrunk] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [newTrunk, setNewTrunk] = useState({
    name: '',
    provider: '',
    host: '',
    port: 5060,
    transport: 'UDP',
    username: '',
    password: '',
    channels: 30,
    inboundDid: '',
    outboundCid: '',
    codec: 'G.711, G.729'
  });

  useEffect(() => {
    fetchTrunks();
  }, []);

  const fetchTrunks = async () => {
    try {
      setLoading(true);
      const response = await sipTrunksAPI.list();
      const transformed = response.data.map(trunk => ({
        id: trunk.id,
        name: trunk.name,
        provider: trunk.provider,
        type: trunk.type,
        host: trunk.host,
        status: trunk.status,
        channels: trunk.channels,
        usedChannels: trunk.used_channels,
        inboundDid: trunk.inbound_did,
        outboundCid: trunk.outbound_cid,
        registered: trunk.registered,
        codec: trunk.codec
      }));
      setSipTrunks(transformed);
    } catch (error) {
      console.error('Error fetching SIP trunks:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTrunks = sipTrunks.filter((trunk) =>
    trunk.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    trunk.provider.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleProviderSelect = (providerId) => {
    const provider = sipProviderTemplates.find((p) => p.id === providerId);
    if (provider) {
      setSelectedProvider(provider);
      setNewTrunk({
        ...newTrunk,
        provider: provider.name,
        host: provider.host,
        port: provider.port,
        transport: provider.transport,
        codec: provider.codecs.join(', ')
      });
    }
  };

  const handleAddTrunk = async () => {
    try {
      await sipTrunksAPI.create({
        name: newTrunk.name,
        provider: newTrunk.provider,
        type: 'SIP',
        host: newTrunk.host,
        port: newTrunk.port,
        transport: newTrunk.transport,
        username: newTrunk.username,
        password: newTrunk.password,
        channels: newTrunk.channels,
        inbound_did: newTrunk.inboundDid,
        outbound_cid: newTrunk.outboundCid,
        codec: newTrunk.codec
      });
      await fetchTrunks();
      setIsAddModalOpen(false);
      setNewTrunk({
        name: '',
        provider: '',
        host: '',
        port: 5060,
        transport: 'UDP',
        username: '',
        password: '',
        channels: 30,
        inboundDid: '',
        outboundCid: '',
        codec: 'G.711, G.729'
      });
      setSelectedProvider(null);
    } catch (error) {
      console.error('Error creating trunk:', error);
    }
  };

  const handleEditTrunk = async () => {
    try {
      await sipTrunksAPI.update(selectedTrunk.id, {
        name: selectedTrunk.name,
        host: selectedTrunk.host,
        channels: selectedTrunk.channels,
        inbound_did: selectedTrunk.inboundDid,
        outbound_cid: selectedTrunk.outboundCid
      });
      await fetchTrunks();
      setIsEditModalOpen(false);
      setSelectedTrunk(null);
    } catch (error) {
      console.error('Error updating trunk:', error);
    }
  };

  const handleDeleteTrunk = async (id) => {
    try {
      await sipTrunksAPI.delete(id);
      await fetchTrunks();
    } catch (error) {
      console.error('Error deleting trunk:', error);
    }
  };

  const handleTestTrunk = async (id) => {
    try {
      const response = await sipTrunksAPI.test(id);
      alert(`Testing trunk... ${response.data.message}`);
      await fetchTrunks();
    } catch (error) {
      alert('Trunk test failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getProviderLogo = (provider) => {
    switch (provider.toLowerCase()) {
      case 'twilio':
        return (
          <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center text-white font-bold text-xs">
            TW
          </div>
        );
      case 'amazon chime':
        return (
          <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold text-xs">
            AWS
          </div>
        );
      case 'bandwidth':
        return (
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-xs">
            BW
          </div>
        );
      case 'voip.ms':
        return (
          <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center text-white font-bold text-xs">
            VM
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 bg-gray-600 rounded-lg flex items-center justify-center text-white font-bold text-xs">
            SIP
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">SIP Trunks</h1>
          <p className="text-gray-400 mt-1">Configure and manage SIP trunk connections</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={() => setIsAddModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add SIP Trunk
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Total Trunks</p>
                <p className="text-2xl font-bold text-white">{sipTrunks.length}</p>
              </div>
              <div className="p-3 bg-blue-500/20 rounded-lg">
                <Phone className="w-5 h-5 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Online</p>
                <p className="text-2xl font-bold text-green-400">
                  {sipTrunks.filter((t) => t.status === 'online').length}
                </p>
              </div>
              <div className="p-3 bg-green-500/20 rounded-lg">
                <Wifi className="w-5 h-5 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Offline</p>
                <p className="text-2xl font-bold text-red-400">
                  {sipTrunks.filter((t) => t.status === 'offline').length}
                </p>
              </div>
              <div className="p-3 bg-red-500/20 rounded-lg">
                <WifiOff className="w-5 h-5 text-red-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Active Channels</p>
                <p className="text-2xl font-bold text-white">
                  {sipTrunks.reduce((acc, t) => acc + t.usedChannels, 0)}
                </p>
              </div>
              <div className="p-3 bg-purple-500/20 rounded-lg">
                <Settings className="w-5 h-5 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="bg-[#12121a] border-[#1e1e2d]">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                placeholder="Search trunks by name or provider..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#1e1e2d] border-[#2a2a3d] text-white placeholder-gray-500"
              />
            </div>
            <Button variant="ghost" className="text-gray-400 hover:text-white">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Trunks List */}
      <div className="grid gap-4">
        {filteredTrunks.map((trunk) => (
          <Card key={trunk.id} className="bg-[#12121a] border-[#1e1e2d] hover:border-[#2a2a3d] transition-colors">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {getProviderLogo(trunk.provider)}
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold text-white">{trunk.name}</h3>
                      <Badge className={trunk.status === 'online'
                        ? 'bg-green-500/20 text-green-400 border-green-500/30'
                        : 'bg-red-500/20 text-red-400 border-red-500/30'
                      }>
                        {trunk.status === 'online' ? (
                          <><CheckCircle className="w-3 h-3 mr-1" /> Online</>
                        ) : (
                          <><XCircle className="w-3 h-3 mr-1" /> Offline</>
                        )}
                      </Badge>
                      {trunk.registered && (
                        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                          Registered
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-400 mt-1">
                      {trunk.provider} • {trunk.host}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-8">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">
                      {trunk.usedChannels}<span className="text-gray-500">/{trunk.channels}</span>
                    </p>
                    <p className="text-xs text-gray-500">Channels</p>
                  </div>

                  <div className="text-right">
                    <p className="text-sm text-white font-mono">{trunk.inboundDid}</p>
                    <p className="text-xs text-gray-500">Inbound DID</p>
                  </div>

                  <div className="text-right">
                    <p className="text-sm text-white font-mono">{trunk.outboundCid}</p>
                    <p className="text-xs text-gray-500">Outbound CID</p>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleTestTrunk(trunk.id)}
                      className="border-[#2a2a3d] text-gray-400 hover:text-white hover:bg-[#1e1e2d]"
                    >
                      <TestTube className="w-4 h-4 mr-1" />
                      Test
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-white">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[#1e1e2d] border-[#2a2a3d]">
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedTrunk(trunk);
                            setIsEditModalOpen(true);
                          }}
                          className="text-gray-300 hover:text-white hover:bg-[#2a2a3d] cursor-pointer"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDeleteTrunk(trunk.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/20 cursor-pointer"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-[#1e1e2d] flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Type:</span>
                  <span className="text-xs text-white">{trunk.type}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Codecs:</span>
                  <span className="text-xs text-white">{trunk.codec}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredTrunks.length === 0 && (
          <Card className="bg-[#12121a] border-[#1e1e2d]">
            <CardContent className="p-12 text-center">
              <Phone className="w-12 h-12 mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400">No SIP trunks found</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add SIP Trunk Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="bg-[#12121a] border-[#1e1e2d] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Add SIP Trunk</DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="provider" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-[#1e1e2d]">
              <TabsTrigger value="provider" className="data-[state=active]:bg-blue-600">Select Provider</TabsTrigger>
              <TabsTrigger value="manual" className="data-[state=active]:bg-blue-600">Manual Setup</TabsTrigger>
            </TabsList>

            <TabsContent value="provider" className="mt-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                {sipProviderTemplates.map((provider) => (
                  <button
                    key={provider.id}
                    onClick={() => handleProviderSelect(provider.id)}
                    className={`p-4 rounded-lg border transition-all ${
                      selectedProvider?.id === provider.id
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-[#2a2a3d] bg-[#1e1e2d] hover:border-[#3a3a4d]'
                    }`}
                  >
                    <div className="text-center">
                      <div className="w-12 h-12 mx-auto mb-2 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                        <Phone className="w-6 h-6 text-white" />
                      </div>
                      <p className="text-white font-medium">{provider.name}</p>
                      <p className="text-xs text-gray-500 mt-1">{provider.transport}</p>
                    </div>
                  </button>
                ))}
              </div>

              {selectedProvider && (
                <div className="space-y-4 pt-4 border-t border-[#1e1e2d]">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-400">Trunk Name</Label>
                      <Input
                        value={newTrunk.name}
                        onChange={(e) => setNewTrunk({ ...newTrunk, name: e.target.value })}
                        placeholder="My Twilio Trunk"
                        className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-400">Channels</Label>
                      <Input
                        type="number"
                        value={newTrunk.channels}
                        onChange={(e) => setNewTrunk({ ...newTrunk, channels: parseInt(e.target.value) })}
                        className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-400">Username / Account SID</Label>
                      <Input
                        value={newTrunk.username}
                        onChange={(e) => setNewTrunk({ ...newTrunk, username: e.target.value })}
                        placeholder="ACxxxxxxxx"
                        className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-400">Password / Auth Token</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={newTrunk.password}
                          onChange={(e) => setNewTrunk({ ...newTrunk, password: e.target.value })}
                          className="bg-[#1e1e2d] border-[#2a2a3d] text-white pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-400">Inbound DID</Label>
                      <Input
                        value={newTrunk.inboundDid}
                        onChange={(e) => setNewTrunk({ ...newTrunk, inboundDid: e.target.value })}
                        placeholder="+1-555-123-4567"
                        className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-400">Outbound Caller ID</Label>
                      <Input
                        value={newTrunk.outboundCid}
                        onChange={(e) => setNewTrunk({ ...newTrunk, outboundCid: e.target.value })}
                        placeholder="+1-555-123-4567"
                        className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                      />
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="manual" className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-400">Trunk Name</Label>
                  <Input
                    value={newTrunk.name}
                    onChange={(e) => setNewTrunk({ ...newTrunk, name: e.target.value })}
                    placeholder="My SIP Trunk"
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Provider Name</Label>
                  <Input
                    value={newTrunk.provider}
                    onChange={(e) => setNewTrunk({ ...newTrunk, provider: e.target.value })}
                    placeholder="Custom Provider"
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label className="text-gray-400">SIP Host / Domain</Label>
                  <Input
                    value={newTrunk.host}
                    onChange={(e) => setNewTrunk({ ...newTrunk, host: e.target.value })}
                    placeholder="sip.provider.com"
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Port</Label>
                  <Input
                    type="number"
                    value={newTrunk.port}
                    onChange={(e) => setNewTrunk({ ...newTrunk, port: parseInt(e.target.value) })}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-400">Transport</Label>
                  <Select
                    value={newTrunk.transport}
                    onValueChange={(value) => setNewTrunk({ ...newTrunk, transport: value })}
                  >
                    <SelectTrigger className="bg-[#1e1e2d] border-[#2a2a3d] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                      <SelectItem value="UDP">UDP</SelectItem>
                      <SelectItem value="TCP">TCP</SelectItem>
                      <SelectItem value="TLS">TLS</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Channels</Label>
                  <Input
                    type="number"
                    value={newTrunk.channels}
                    onChange={(e) => setNewTrunk({ ...newTrunk, channels: parseInt(e.target.value) })}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-400">Username</Label>
                  <Input
                    value={newTrunk.username}
                    onChange={(e) => setNewTrunk({ ...newTrunk, username: e.target.value })}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={newTrunk.password}
                      onChange={(e) => setNewTrunk({ ...newTrunk, password: e.target.value })}
                      className="bg-[#1e1e2d] border-[#2a2a3d] text-white pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-400">Inbound DID</Label>
                  <Input
                    value={newTrunk.inboundDid}
                    onChange={(e) => setNewTrunk({ ...newTrunk, inboundDid: e.target.value })}
                    placeholder="+1-555-123-4567"
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Outbound Caller ID</Label>
                  <Input
                    value={newTrunk.outboundCid}
                    onChange={(e) => setNewTrunk({ ...newTrunk, outboundCid: e.target.value })}
                    placeholder="+1-555-123-4567"
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setIsAddModalOpen(false)} className="border-[#2a2a3d] text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleAddTrunk} className="bg-blue-600 hover:bg-blue-700">
              Add Trunk
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit SIP Trunk Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="bg-[#12121a] border-[#1e1e2d] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl">Edit SIP Trunk</DialogTitle>
          </DialogHeader>
          {selectedTrunk && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-gray-400">Trunk Name</Label>
                <Input
                  value={selectedTrunk.name}
                  onChange={(e) => setSelectedTrunk({ ...selectedTrunk, name: e.target.value })}
                  className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-400">Host</Label>
                  <Input
                    value={selectedTrunk.host}
                    onChange={(e) => setSelectedTrunk({ ...selectedTrunk, host: e.target.value })}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Channels</Label>
                  <Input
                    type="number"
                    value={selectedTrunk.channels}
                    onChange={(e) => setSelectedTrunk({ ...selectedTrunk, channels: parseInt(e.target.value) })}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-400">Inbound DID</Label>
                  <Input
                    value={selectedTrunk.inboundDid}
                    onChange={(e) => setSelectedTrunk({ ...selectedTrunk, inboundDid: e.target.value })}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Outbound CID</Label>
                  <Input
                    value={selectedTrunk.outboundCid}
                    onChange={(e) => setSelectedTrunk({ ...selectedTrunk, outboundCid: e.target.value })}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)} className="border-[#2a2a3d] text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleEditTrunk} className="bg-blue-600 hover:bg-blue-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SIPTrunks;
